<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <div class="section has-text-centered">
        <h1 class="title">Manage Channel Schedule</h1>
      </div>
      <div class="columns is-vcentered">
        <div class="column is-12">
          <div class="flex-card light-bordered light-raised">
            <div class="has-margin-bottom-15 buttons">
              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="addToSchedule"
              >
                Add Scheduled Title</b-button
              >
              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportEpg"
              >
                Export EPG</b-button
              >
              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportNielsen"
              >
                Export Nielsen</b-button
              >
              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportBreakSchedule"
              >
                Export Break Schedule</b-button
              >
              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="exportProduction"
              >
                Export Production</b-button
              >
              <b-button
                type="is-primary"
                :disabled="loading"
                :class="{ 'is-loading': loading }"
                @click="copyWeek"
              >
                Copy Week</b-button
              >
            </div>
            <div class="card-body">
              <VueCal
                style="height: 500px"
                :disable-views="['years', 'year', 'month']"
                :time-step="30"
                :events="events"
                :cell-click-hold="false"
                :drag-to-create-event="false"
                :on-event-click="eventClick"
                :snap-to-time="30"
                :on-event-create="eventCreate"
                :editable-events="{
                  drag: true,
                  delete: true,
                  resize: false,
                  create: true,
                }"
                @cell-dblclick="addEventOnClick"
                @ready="fetchEvents"
                @view-change="fetchEvents"
                @event-drop="eventDrop"
                :special-hours="specialHours"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <ExportScheduleModal
      v-if="showExportScheduleModal"
      :show="showExportScheduleModal"
      :start="lastStartDate"
      :end="lastEndDate"
      :channel-id="channelId"
      :report-type="reportType"
      :channel-name="channel.name"
      @close-modal="showExportScheduleModal = false"
    >
    </ExportScheduleModal>
    <AddToScheduleModal
      v-if="showAddToScheduleModal"
      :show="showAddToScheduleModal"
      :start="start"
      :channel-id="channelId"
      :item="item"
      @refresh="refreshData"
      @close-modal="showAddToScheduleModal = false"
    ></AddToScheduleModal>
    <CopyScheduleWeekModal
      v-if="showCopyScheduleWeekModal"
      :show="showCopyScheduleWeekModal"
      :start-date="lastStartDate"
      :end-date="lastEndDate"
      :channel-id="channelId"
      @refresh="refreshData"
      @close-modal="showCopyScheduleWeekModal = false"
    ></CopyScheduleWeekModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { getHours, getMinutes } from 'date-fns'
import ScheduleApi from '@/api/schedules'
import CardComponent from '@/components/CardComponent'
import AddToScheduleModal from '@/components/AddToScheduleModal'
import ExportScheduleModal from '@/components/ExportScheduleModal'
import CopyScheduleWeekModal from '@/components/CopyScheduleWeekModal'
import ChannelApi from '@/api/channels'
import ChannelViewingSlotApi from '@/api/channelViewingSlots'
import VueCal from 'vue-cal'
import 'vue-cal/dist/drag-and-drop.js'
import 'vue-cal/dist/vuecal.css'

export default {
  name: 'EditSchedule',
  components: {
    AddToScheduleModal,
    ExportScheduleModal,
    CardComponent,
    VueCal,
    CopyScheduleWeekModal,
  },
  metaInfo: {
    title: 'Edit Schedule',
  },
  computed: {
    ...mapGetters(['user']),
  },
  async mounted() {
    await this.loadSpecialHours()
    this.channel = await ChannelApi.getById(this.channelId)
    this.initialized = true
  },
  data() {
    return {
      loading: false,
      initialized: false,
      showAddToScheduleModal: false,
      showExportScheduleModal: false,
      showCopyScheduleWeekModal: false,
      start: '',
      lastStartDate: '',
      lastEndDate: '',
      channelId: parseInt(this.$route.params.id, 10),
      events: [],
      item: {},
      specialHours: {},
      reportType: '',
      channel: {},
    }
  },
  methods: {
    ...mapActions(['getUser']),
    async loadSpecialHours() {
      const viewingSlots = await ChannelViewingSlotApi.getByChannel(
        this.channelId
      )
      let slotNo = 0
      viewingSlots.forEach((slot) => {
        const days = slot.days.split(',').map((item) => parseInt(item, 10))
        days.forEach((day) => {
          if (!this.specialHours[day]) {
            this.specialHours[day] = []
          }
          this.specialHours[day].push({
            from: this.getSpecialHours(slot.start_time),
            to: this.getSpecialHours(slot.end_time),
            class: `special-hours-${slotNo}`,
          })
        })
        slotNo++
        if (slotNo > 9) {
          slotNo = 0
        }
      })
    },
    getSpecialHours(date) {
      date = new Date(date)
      return getHours(date) * 60 + getMinutes(date)
    },
    eventCreate(event, deleteFn) {
      deleteFn()
    },
    async eventDrop({ event, originalEvent, external }) {
      event.start_time = event.start
      event.end_time = event.end
      await ScheduleApi.update(event)
      this.$buefy.toast.open({
        message: 'Schedule updated successfully!',
        position: 'is-top',
        type: 'is-success',
      })
    },
    eventClick(currentevent) {
      this.start = currentevent.start
      this.item = currentevent
      this.showAddToScheduleModal = true
    },
    async refreshData() {
      await this.fetchEvents({
        startDate: this.lastStartDate,
        endDate: this.lastEndDate,
      })
    },
    async exportEpg() {
      this.showExportScheduleModal = true
      this.reportType = 'epg'
    },
    async exportNielsen() {
      this.showExportScheduleModal = true
      this.reportType = 'nielsen'
    },
    async exportBreakSchedule() {
      this.showExportScheduleModal = true
      this.reportType = 'break'
    },
    async exportProduction() {
      this.showExportScheduleModal = true
      this.reportType = 'production'
    },
    async fetchEvents({ view, startDate, endDate, week }) {
      this.lastStartDate = startDate
      this.lastEndDate = endDate
      let items = await ScheduleApi.getByChannel(
        this.$route.params.id,
        startDate,
        endDate
      )
      let slotNo = 0
      this.events = items.map((item) => {
        let mediadata = null
        if (item.mediadata) {
          mediadata = JSON.parse(item.mediadata)
        }
        slotNo++
        if (slotNo > 9) {
          slotNo = 0
        }
        return {
          title:
            item.media_title +
            (item.media_type === 'series' && mediadata && mediadata.seriesNo
              ? ` S${mediadata.seriesNo}`
              : '') +
            (item.episode_number ? ` E${item.episode_number}` : ''),
          start: new Date(item.start_time),
          end: new Date(item.end_time),
          media_id: item.media_id,
          episode_id: item.episode_id,
          id: item.id,
          class: `${item.media_type}-${slotNo}`,
          repeats_id: item.repeats_id,
        }
      })
    },
    addToSchedule() {
      this.start = null
      this.item = undefined
      this.showAddToScheduleModal = true
    },
    addEventOnClick($event) {
      this.start = $event
      this.item = undefined
      this.showAddToScheduleModal = true
    },
    copyWeek() {
      this.showCopyScheduleWeekModal = true
    },
  },
}
</script>

<style>
.vuecal__no-event {
  display: none;
}
.vuecal__cell .vuecal__event * {
  font-size: 12px;
}
.vuecal__event-title {
  font-weight: bold;
}
.vuecal__event {
  color: #fff;
  border: 1px solid #666;
  opacity: 0.8;
}
.vuecal__event.series-1 {
  background-color: #3fe0d0;
}
.vuecal__event.series-2 {
  background-color: #4169e1;
}
.vuecal__event.series-3 {
  background-color: #6595ec;
}
.vuecal__event.series-4 {
  background-color: #0047ab;
}
.vuecal__event.series-5 {
  background-color: #aed8e6;
}
.vuecal__event.series-6 {
  background-color: #89ceef;
}
.vuecal__event.series-7 {
  background-color: #0ebab5;
}
.vuecal__event.series-8 {
  background-color: #0d52ba;
}
.vuecal__event.series-9 {
  background-color: #0073cf;
}
.vuecal__event.series-0 {
  background-color: #2a52be;
}
.vuecal__event.movie-1 {
  background-color: #8d021f;
}
.vuecal__event.movie-2 {
  background-color: #5e1914;
}
.vuecal__event.movie-3 {
  background-color: #b80f0a;
}
.vuecal__event.movie-4 {
  background-color: #f20d09;
}
.vuecal__event.movie-5 {
  background-color: #bf0a30;
}
.vuecal__event.movie-6 {
  background-color: #ca3433;
}
.vuecal__event.movie-7 {
  background-color: #d21f3c;
}
.vuecal__event.movie-8 {
  background-color: #b43757;
}
.vuecal__event.movie-9 {
  background-color: #ff0800;
}
.vuecal__event.movie-0 {
  background-color: #7e191b;
}
.vuecal__cell .special-hours-1 {
  background: #fff;
}
.vuecal__cell .special-hours-2 {
  background: #efefef;
}
.vuecal__cell .special-hours-3 {
  background: #eee;
}
.vuecal__cell .special-hours-4 {
  background: #ddd;
}
.vuecal__cell .special-hours-5 {
  background: #ccc;
}
.vuecal__cell .special-hours-6 {
  background: #bbb;
}
.vuecal__cell .special-hours-7 {
  background: #aaa;
}
.vuecal__cell .special-hours-8 {
  background: #999;
}
.vuecal__cell .special-hours-9 {
  background: #989898;
}
.vuecal__cell .special-hours-0 {
  background: #979797;
}
</style>
